import Load from 'components/Load';
import { NavigationalLayoutElement } from 'modules/app/components/NavigationalLayout';
import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { RouteDescriptor, createRouteHandle } from 'routing/types';
import basePath from 'utils/basePath';
import { isCordova } from 'utils/platform';

const AsyncHostHome = Load(
  () => import(/* webpackChunkName: "host-homescreen" */ './pages/HostHome'),
);
const AsyncScheduleBreakFlow = Load(
  () =>
    import(
      /* webpackChunkName: "host-homescreen" */ '../host/components/HostScheduleBreak/ScheduleBreakFlow'
    ),
);
const AsyncHostStatusFlow = Load(
  () =>
    import(
      /* webpackChunkName: "host-homescreen" */ '../host/components/HostStatus/HostStatusFlow'
    ),
);
const AsyncFeedbackContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-homescreen" */ './containers/FeedbackContainer'
    ),
);

const AsyncHostPerformance = Load(
  () =>
    import(
      /* webpackChunkName: "host-homescreen" */ './components/HostPerformance/index'
    ),
);

const AsyncHostScoresAndRewards = Load(
  () =>
    import(
      /* webpackChunkName: "host-homescreen" */ './components/HostScoresAndRewards'
    ),
);

export const hostHomeRoute: RouteDescriptor = {
  id: 'host-homescreen.home',
  path: `${basePath}/host/home`,
  label: 'Host Home',
  audience: 'host',
  layout: 'navigational',
  module: 'host-homescreen',
};

export const hostDashboardRedirectRoute: RouteDescriptor = {
  id: 'host-homescreen.dashboard-redirect',
  path: `${basePath}/host/dashboard`,
  label: 'Host Dashboard Redirect',
  audience: 'host',
  layout: 'standalone',
  module: 'host-homescreen',
};

export const hostScheduleBreakRoute: RouteDescriptor = {
  id: 'host-homescreen.schedule-break',
  path: `${basePath}/host/schedule-break`,
  label: 'Host Schedule Break',
  audience: 'host',
  layout: 'navigational',
  module: 'host-homescreen',
};

export const hostScheduleBreakRedirectRoute: RouteDescriptor = {
  id: 'host-homescreen.schedule-break-redirect',
  path: `${basePath}/host/schedule-break/*`,
  label: 'Host Schedule Break Redirect',
  audience: 'host',
  layout: 'standalone',
  module: 'host-homescreen',
};

export const hostStatusRoute: RouteDescriptor = {
  id: 'host-homescreen.status',
  path: `${basePath}/host/status/:state?`,
  label: 'Host Status',
  audience: 'host',
  layout: 'navigational',
  module: 'host-homescreen',
};

export const hostGoOfflineRoute: RouteDescriptor = {
  id: 'host-homescreen.go-offline',
  path: `${basePath}/host/go-offline`,
  label: 'Host Go Offline',
  audience: 'host',
  layout: 'navigational',
  module: 'host-homescreen',
};

export const hostFeedbackRoute: RouteDescriptor = {
  id: 'host-homescreen.feedback',
  path: `${basePath}/host/feedback`,
  label: 'Host Feedback',
  audience: 'host',
  layout: 'navigational',
  module: 'host-homescreen',
};

export const hostScoresAndRewardsRoute: RouteDescriptor = {
  id: 'host-homescreen.scores-and-rewards',
  path: `${basePath}/host/performance/scores-and-rewards/*`,
  label: 'Host Scores and Rewards',
  audience: 'host',
  layout: 'navigational',
  module: 'host-homescreen',
};

export const hostPerformanceRoute: RouteDescriptor = {
  id: 'host-homescreen.performance',
  path: `${basePath}/host/performance/:metric/:view?`,
  label: 'Host Performance',
  audience: 'host',
  layout: 'navigational',
  module: 'host-homescreen',
};

export const HostHomePageRoutes = (
  <Route
    key={hostHomeRoute.id}
    id={hostHomeRoute.id}
    path={hostHomeRoute.path}
    handle={createRouteHandle(hostHomeRoute)}
    element={
      <NavigationalLayoutElement
        hideFooter
        component={AsyncHostHome as any}
        showBottomNavigation
        noNavbar={isCordova()}
        showSearchInNavbar={false}
      />
    }
  />
);

export const HostHomePageWidgetRoutes = [
  <Route
    key={hostDashboardRedirectRoute.id}
    id={hostDashboardRedirectRoute.id}
    path={hostDashboardRedirectRoute.path}
    handle={createRouteHandle(hostDashboardRedirectRoute)}
    element={<Navigate to={hostHomeRoute.path} replace />}
  />,
  <Route
    key={hostScheduleBreakRoute.id}
    id={hostScheduleBreakRoute.id}
    path={hostScheduleBreakRoute.path}
    handle={createRouteHandle(hostScheduleBreakRoute)}
    element={
      <NavigationalLayoutElement
        hideFooter
        component={AsyncScheduleBreakFlow as any}
        onTopOf={AsyncHostHome as any}
        showBottomNavigation
        noNavbar={isCordova()}
        showSearchInNavbar={false}
      />
    }
  />,
  <Route
    key={hostScheduleBreakRedirectRoute.id}
    id={hostScheduleBreakRedirectRoute.id}
    path={hostScheduleBreakRedirectRoute.path}
    handle={createRouteHandle(hostScheduleBreakRedirectRoute)}
    element={<Navigate to={hostScheduleBreakRoute.path} replace />}
  />,
  <Route
    key={hostStatusRoute.id}
    id={hostStatusRoute.id}
    path={hostStatusRoute.path}
    handle={createRouteHandle(hostStatusRoute)}
    element={
      <NavigationalLayoutElement
        hideFooter
        component={AsyncHostStatusFlow as any}
        onTopOf={AsyncHostHome as any}
        showBottomNavigation
        noNavbar={isCordova()}
        showSearchInNavbar={false}
      />
    }
  />,
  <Route
    key={hostGoOfflineRoute.id}
    id={hostGoOfflineRoute.id}
    path={hostGoOfflineRoute.path}
    handle={createRouteHandle(hostGoOfflineRoute)}
    element={
      <NavigationalLayoutElement
        hideFooter
        component={AsyncHostStatusFlow as any}
        onTopOf={AsyncHostHome as any}
        showBottomNavigation
        noNavbar={isCordova()}
        showSearchInNavbar={false}
      />
    }
  />,
  <Route
    key={hostFeedbackRoute.id}
    id={hostFeedbackRoute.id}
    path={hostFeedbackRoute.path}
    handle={createRouteHandle(hostFeedbackRoute)}
    element={
      <NavigationalLayoutElement
        hideFooter
        component={AsyncFeedbackContainer as any}
        showBottomNavigation
        noNavbar={isCordova()}
        nonFixedNavbar
      />
    }
  />,
  <Route
    key={hostScoresAndRewardsRoute.id}
    id={hostScoresAndRewardsRoute.id}
    path={hostScoresAndRewardsRoute.path}
    handle={createRouteHandle(hostScoresAndRewardsRoute)}
    element={
      <NavigationalLayoutElement
        hideFooter
        component={AsyncHostScoresAndRewards as any}
        showBottomNavigation
        noNavbar={isCordova()}
        nonFixedNavbar
      />
    }
  />,
  <Route
    key={hostPerformanceRoute.id}
    id={hostPerformanceRoute.id}
    path={hostPerformanceRoute.path}
    handle={createRouteHandle(hostPerformanceRoute)}
    element={
      <NavigationalLayoutElement
        hideFooter
        component={AsyncHostPerformance as any}
        showBottomNavigation
        noNavbar={isCordova()}
        nonFixedNavbar
      />
    }
  />,
];

export const hostHomescreenRouteDescriptors = [
  hostHomeRoute,
  hostDashboardRedirectRoute,
  hostScheduleBreakRoute,
  hostScheduleBreakRedirectRoute,
  hostStatusRoute,
  hostGoOfflineRoute,
  hostFeedbackRoute,
  hostScoresAndRewardsRoute,
  hostPerformanceRoute,
];
